/**
 * Whether the cell under the pointer would ACCEPT this drop — decided while the
 * drag is still in the air.
 *
 * The rules that refuse a drop already existed (`resolveDripStepDrop`, and the
 * campaign-status guard in `onDripStepDropThunk`), but they only ran on release.
 * Every cell therefore highlighted the same way, the user let go on an
 * impossible one, and got either nothing at all or a modal explaining a move
 * they had already made in their head. Both teach the same wrong lesson — that
 * the feature is unreliable — because the screen said "yes" right up until it
 * said "no".
 *
 * Running the same rules on every pointer move lets the grid answer DURING the
 * gesture: the cell stops inviting the drop and the card at the cursor says, in
 * one sentence, what the sequence will not allow. Nothing is written either way;
 * this is the same pure decision, taken earlier.
 *
 * Deliberately conservative: when the drip document is not in the store we
 * return `null` (no claim) rather than guessing. A refusal shown wrongly is
 * worse than a refusal shown late — it stops a move that was in fact legal.
 */
import { resolveDripStepDrop } from './drip-drag';
import type { DripDropRefusal, DripStepLike } from './drip-drag';

export type DropPreviewRefusal = DripDropRefusal | 'step-started';

export interface DripDropPreviewInput {
    /** The sequence's first send, in ms. `null` when it is not known yet. */
    startMs: number | null;
    steps: ReadonlyArray<DripStepLike> | null | undefined;
    stepIndex: number;
    /** Where the pointer is now. `null` while it is off the grid. */
    targetMs: number | null;
    /** This step's campaign has left `pending` — its send times are frozen. */
    stepHasStarted?: boolean;
    isRecurringDrip?: boolean;
}

/**
 * `null` means "nothing to say": the drop is allowed, or we cannot tell.
 */
export function previewDripStepDrop(input: DripDropPreviewInput): DropPreviewRefusal | null {
    const { startMs, steps, stepIndex, targetMs, stepHasStarted, isRecurringDrip } = input;

    // A frozen step is refused wherever it is dropped, so it is worth saying
    // before the target is even known.
    if (stepHasStarted) return 'step-started';

    if (targetMs == null) return null;
    if (startMs == null || Number.isNaN(startMs)) return null;
    if (!Array.isArray(steps) || steps.length === 0) return null;

    const resolution = resolveDripStepDrop({
        startMs, steps, stepIndex, targetMs, isRecurringDrip,
    });
    return resolution.kind === 'refused' ? resolution.reason : null;
}

/**
 * The sentence a person reads, keyed by reason.
 *
 * These are deliberately NOT the model's words. `'before-previous-step'` names a
 * position in an array; what the user needs to know is that a follow-up is
 * defined as coming after the message it follows, so there is no such thing as
 * one that arrives first.
 */
export const DROP_PREVIEW_REFUSAL_KEY: Record<DropPreviewRefusal, string> = {
    'before-previous-step': 'calendar.grid.dripRefusalBeforePrevious',
    'unknown-step': 'calendar.grid.dropErrorGeneric',
    'step-started': 'calendar.grid.dripErrorStarted',
};
